import type { HelpDialKind, HelpGuide, HelpRequirement } from "./types";

/**
 * What the page currently showing a dial knows about the person looking at it. Set by `Workspace.tsx`, which
 * is the only place that holds all three answers, and cleared on the way out.
 */
export interface HelpAudienceState {
  /**
   * A PIN-unlocked kiosk session or a guest. Both lose the header's Feedback button, and neither is ever
   * auto-offered a tour — `InstanceScopeMiddleware` is an allowlist that admits exactly `/api/me` and
   * `/api/me/notifications` for them, so `/api/me/tours` would 403 and widening that for a tour flag is not a
   * trade worth making. The Help drawer still works, so the tour stays reachable, just never unprompted.
   */
  confined: boolean;
  /** Whether the subscribe controls exist on this host at all. */
  canSubscribe: boolean;
  /** Whether there is anywhere on this dial this person could actually set a status — see `audience.ts`. */
  canSetStatus: boolean;
  /** Whether this dial shows calendars at all — it has a section of days (KEY-736). */
  hasCalendars: boolean;
}

/** Who the guide is about to be shown to: which dial they have open, and what they can do on it. */
export interface HelpAudience {
  /** The kind of dial open right now, or null off a dial (dashboard, groups, settings). */
  dialKind: HelpDialKind | null;
  can: (requirement: HelpRequirement) => boolean;
}

/**
 * Cut a guide down to the steps that apply to this audience (KEY-535).
 *
 * Pure, and resolved ONCE at `start()` rather than while rendering. `HelpOverlay` reads "Step n of
 * `guide.steps.length`" straight off the guide it is handed, so filtering at render time would mis-number
 * every step and make Back/Next walk a list the counter doesn't describe. The provider therefore stores the
 * RESOLVED guide, and everything downstream sees a guide that is simply shorter.
 *
 * A step is kept when:
 * - it claims no dial kind, or claims the one that's open. A step tagged for a kind is dropped when the
 *   audience's kind is `null` as well — see the note on `HelpStep.dialKind`; and
 * - the audience can do everything the step `requires`.
 *
 * The result keeps the guide's id, title and summary, so the launcher and the overlay header are unchanged.
 */
export function resolveGuide(guide: HelpGuide, audience: HelpAudience): HelpGuide {
  const steps = guide.steps.filter(
    (step) =>
      (step.dialKind === undefined || step.dialKind === audience.dialKind) &&
      (step.requires ?? []).every((requirement) => audience.can(requirement)),
  );
  // Same object when nothing was dropped, so the common case doesn't churn a new guide identity through the
  // overlay's effects on every start.
  return steps.length === guide.steps.length ? guide : { ...guide, steps };
}

/**
 * Turn what the dial page knows into the question `resolveGuide` asks.
 *
 * With no state — off a dial, or before it has loaded — every requirement passes. That is deliberate: a
 * guide started by hand from the drawer should keep the steps whose requirements we simply cannot evaluate,
 * and the only thing worth dropping there is the pair of mutually exclusive dial-kind steps, which
 * `resolveGuide` drops on its own because the kind is null.
 */
export function audienceFor(state: HelpAudienceState | null, dialKind: HelpDialKind | null): HelpAudience {
  return {
    dialKind,
    can: (requirement) => {
      if (!state) {
        return true;
      }
      switch (requirement) {
        case "subscribe":
          return state.canSubscribe;
        case "setStatus":
          return state.canSetStatus;
        case "feedback":
          return !state.confined;
        case "calendars":
          return state.hasCalendars;
      }
    },
  };
}
